import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { Card, CardContent, CardHeader, CardTitle, Badge } from '@org/ui';
import { FeedbackView, parseFeedbackContent } from '../../components/feedback-view';

export const Route = createFileRoute('/_app/feedback')({
  component: FeedbackRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    // Set by the "your feedback is ready" notification: which report to open.
    feedbackId: typeof search.feedbackId === 'string' && search.feedbackId.length > 0 ? search.feedbackId : undefined,
  }),
});

function FeedbackRoute() {
  const { t } = useTranslation('admin');
  const { feedbackId: requestedFeedbackId } = Route.useSearch();
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: feedbackList, isLoading: isListLoading } = trpc.dashboard.studentFeedback.list.useQuery();

  const { data: selectedFeedback, isLoading: isDetailLoading } = trpc.dashboard.studentFeedback.get.useQuery(
    { feedbackId: selectedFeedbackId! },
    { enabled: !!selectedFeedbackId },
  );

  // The classes of the report's period, as they stand now: one dot each for
  // attendance and homework; a homework dot opens the class so the student can
  // finish what is missing.
  const { data: sessionsData } = trpc.dashboard.studentFeedback.sessions.useQuery(
    { feedbackId: selectedFeedbackId! },
    { enabled: !!selectedFeedbackId, staleTime: 60_000 },
  );

  // Open the requested feedback (deep link) when it exists, else the newest.
  useEffect(() => {
    if (!feedbackList?.length) return;
    const requested = requestedFeedbackId
      ? feedbackList.find((item) => item.feedbackId === requestedFeedbackId)
      : undefined;
    if (requested) {
      setSelectedFeedbackId((current) => (current === requested.feedbackId ? current : requested.feedbackId));
      return;
    }
    if (!selectedFeedbackId) {
      setSelectedFeedbackId(feedbackList[0].feedbackId);
    }
  }, [feedbackList, selectedFeedbackId, requestedFeedbackId]);

  // Scroll selected card into view
  useEffect(() => {
    if (!selectedFeedbackId || !scrollRef.current) return;
    const el = scrollRef.current.querySelector(`[data-feedback-id="${selectedFeedbackId}"]`) as HTMLElement | null;
    el?.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
  }, [selectedFeedbackId]);

  const parsedContent = selectedFeedback ? parseFeedbackContent(selectedFeedback.content) : null;

  return (
    <div className="space-y-6 pb-10">
      <header className="space-y-2">
        <p className="text-sm uppercase tracking-[0.3em] text-brand">{t('p2e.feedback.report.eyebrow')}</p>
        <h1 className="text-3xl font-semibold text-gray-900">{t('p2e.feedback.report.title')}</h1>
        {/* <p className="text-sm text-gray-600">{t('p2e.feedback.report.description')}</p> */}
      </header>

      {/* Feedback List — horizontal scroll */}
      <section>
        {isListLoading ? (
          <p className="text-sm text-gray-500">{t('common.status.loading', 'Loading...')}</p>
        ) : !feedbackList?.length ? (
          <Card className="rounded-2xl border border-rose-50 bg-white/90 shadow-sm">
            <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
              <p className="text-lg font-semibold text-gray-700">{t('p2e.feedback.report.empty')}</p>
              <p className="text-sm text-gray-500">{t('p2e.feedback.report.emptyDescription')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="-mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10">
            <div
              ref={scrollRef}
              className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-rose-200 scrollbar-track-transparent"
            >
              {feedbackList.map((item) => {
                const isSelected = selectedFeedbackId === item.feedbackId;
                const typeLabel =
                  item.type === 'midterm'
                    ? t('p2e.feedback.report.typeMidterm')
                    : t('p2e.feedback.report.typeFinal');
                const dateRange =
                  item.dateFrom && item.dateTo
                    ? `${new Date(item.dateFrom).toLocaleDateString('pt-BR')} — ${new Date(item.dateTo).toLocaleDateString('pt-BR')}`
                    : null;

                return (
                  <button
                    key={item.feedbackId}
                    type="button"
                    data-feedback-id={item.feedbackId}
                    onClick={() => setSelectedFeedbackId(item.feedbackId)}
                    className="shrink-0 text-left rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                  >
                    <div
                      className={`flex w-[220px] flex-col gap-2 rounded-xl border px-4 py-3 transition-all duration-100 ease-out ${
                        isSelected
                          ? 'border-[#cf2e2e] border-b-[5px] border-b-[#a82424] bg-gradient-to-b from-[#e04545] to-[#cf2e2e] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_6px_0_rgba(0,0,0,0.15),0_8px_16px_rgba(207,46,46,0.3)] translate-y-0'
                          : 'border-rose-200 border-b-[5px] border-b-rose-300 bg-gradient-to-b from-white to-gray-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_6px_0_rgba(0,0,0,0.08),0_8px_16px_rgba(247,141,167,0.15)] hover:translate-y-[3px] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_3px_0_rgba(0,0,0,0.08),0_4px_8px_rgba(247,141,167,0.12)]'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className={`truncate text-sm font-semibold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                          {item.teacherName}
                        </p>
                        <Badge
                          className={`shrink-0 text-xs ${
                            isSelected
                              ? 'bg-white/25 text-white backdrop-blur'
                              : item.type === 'midterm'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-rose-100 text-brand'
                          }`}
                        >
                          {typeLabel}
                        </Badge>
                      </div>
                      {dateRange && (
                        <p className={`text-xs ${isSelected ? 'text-white/75' : 'text-gray-500'}`}>
                          {dateRange}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Selected Feedback Detail */}
      {selectedFeedbackId && (
        <section className="space-y-4">
          <div className="rounded-2xl border border-rose-50 bg-white/90 p-6 shadow-sm">
            {isDetailLoading ? (
              <p className="text-sm text-gray-500">{t('common.status.loading', 'Loading...')}</p>
            ) : selectedFeedback && parsedContent ? (
              <>
                <div className="mb-6 space-y-1">
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-semibold text-gray-700">
                      {t('p2e.feedback.report.teacherLabel')}: {selectedFeedback.teacherName}
                    </p>
                    <Badge
                      className={
                        selectedFeedback.type === 'midterm'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-rose-100 text-brand'
                      }
                    >
                      {selectedFeedback.type === 'midterm'
                        ? t('p2e.feedback.report.typeMidterm')
                        : t('p2e.feedback.report.typeFinal')}
                    </Badge>
                  </div>
                  {selectedFeedback.dateFrom && selectedFeedback.dateTo && (
                    <p className="text-sm text-gray-500">
                      {new Date(selectedFeedback.dateFrom).toLocaleDateString('pt-BR')} —{' '}
                      {new Date(selectedFeedback.dateTo).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                </div>
                <FeedbackView
                  content={parsedContent}
                  sessions={sessionsData?.sessions}
                  classHref={(session) => (session.classId ? `/classes/${session.classId}/block/0` : null)}
                />
              </>
            ) : (
              <p className="text-sm text-gray-500">{t('p2e.feedback.report.empty')}</p>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
